'use client';

import closeIcon from '@iconify/icons-carbon/close';
import playFilledAlt from '@iconify/icons-carbon/play-filled-alt';
import { varZoom } from '@larvitu/shared/framer-motion/utils';
import { Box, CircularProgress } from '@mui/material';
import { Children, cloneElement, useState } from 'react';

import { BgOverlay, DialogAnimate, FabButtonAnimate, IconButtonAnimate, Iconify, Image } from '..';

import type { ImageRatio } from '..';
import type { FC, ReactElement } from 'react';
import type { ReactPlayerProps } from 'react-player';

export interface PlayerControllerProps extends ReactPlayerProps {
  imgPath?: string;
  videoPath: string;
  ratio?: ImageRatio;
  children: ReactElement<ReactPlayerProps>;
}

export const PlayerController: FC<PlayerControllerProps> = ({
  imgPath = '',
  ratio = '16/9',
  videoPath,
  children,
}) => {
  const [loading, setLoading] = useState( true );
  const [open, setOpen] = useState( false );

  const handleOpen = (): void => {
    setOpen( true );
  };

  const handleClose = (): void => {
    setOpen( false );
  };

  const onReady = (): void => {
    setLoading( false );
  };

  return (
    <>
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        <FabButtonAnimate
          size="large"
          color="inherit"
          onClick={handleOpen}
          sx={{ width: 1, height: 1, bgcolor: 'common.white' }}
          sxWrap={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: 64,
            height: 64,
            zIndex: 9,
            m: 'auto',
            position: 'absolute',
          }}
        >
          <Iconify icon={playFilledAlt} sx={{ color: 'primary.main' }} />
        </FabButtonAnimate>

        <BgOverlay />

        {imgPath && <Image alt="hero" src={imgPath} ratio={ratio} />}
      </Box>

      <DialogAnimate
        fullScreen
        open={open}
        variants={{
          initial: { ...varZoom().in.initial, scale: 0.64 },
          animate: {
            ...varZoom().in.animate,
            transition: { ...varZoom().in.animate.transition, duration: 0.48 },
          },
          exit: { ...varZoom().in.exit, scale: 0.64 },
        }}
        sx={{ bgcolor: 'transparent' }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9,
          }}
        >
          <IconButtonAnimate
            size="large"
            onClick={handleClose}
            sx={{
              bgcolor: 'common.white',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'common.white',
              },
            }}
          >
            <Iconify icon={closeIcon} sx={{ width: 24, height: 24 }} />
          </IconButtonAnimate>
        </Box>

        {loading && (
          <CircularProgress
            sx={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              m: 'auto',
              zIndex: -1,
              position: 'absolute',
            }}
          />
        )}

        {Children.map( children, (child) =>
          cloneElement( child, {
            url: videoPath,
            playing: !loading,
            onReady,
          } ) )}
      </DialogAnimate>
    </>
  );
};
